import { getAuth } from 'firebase/auth';
import { config } from '../config';

interface TrainingMessage {
  type: 'session_started' | 'item_delivered' | 'item_response_required' | 'item_evaluated' | 'session_completed' | 'all_items_delivered' | 'error';
  data?: any;
}

interface TrainingItem {
  trackItemId: string;
  itemType: string;
  aiText: string;
  aiAudioBase64?: string;  // Base64 encoded audio stream
  expectedKeywords?: string[];
  landmineKeywords?: string[];
  allowedResponseTime: number;
  // Objection detection results (generated in parallel with TTS)
  detectedObjections?: Array<{
    type: string;
    category: string;
    trigger?: string;
    competitor?: string;
    text: string;
    // Landmine-specific fields
    landmine_type?: 'legal_risk' | 'detractor';
    can_say?: Array<any>;
    cannot_say?: Array<any>;
    quick_tips?: string;
    // Competitor-specific fields
    advantages?: Array<any>;
    weaknesses?: Array<any>;
    talking_points?: string;
  }>;
  competitorsMentioned?: string[];
  landminesTriggered?: number;
  idealAnswer?: string;  // Ideal response for the salesperson (deprecated - use storedAnswer)
  storedAnswer?: string;  // Stored database answer (replaces idealAnswer)
  aiGeneratedAnswer?: string;  // AI-generated answer from LLM
}

interface ItemEvaluation {
  trackItemId: string;
  itemType?: string;
  responseQuality: number;
  keywordsCovered: string[];
  keywordsMissed: string[];
  landminesTriggered: number;
  competitorsMentioned?: string[];
  detectedObjections?: Array<{
    type: string;
    category: string;
    trigger?: string;
    competitor?: string;
    text: string;
    // Landmine-specific fields
    landmine_type?: 'legal_risk' | 'detractor';
    can_say?: Array<any>;
    cannot_say?: Array<any>;
    quick_tips?: string;
    // Competitor-specific fields
    advantages?: Array<any>;
    weaknesses?: Array<any>;
    talking_points?: string;
  }>;
  aiEvaluation: string;
  aiFollowUp?: string;
  aiFollowUpAudioBase64?: string;  // Base64 encoded follow-up audio
}

interface SessionResult {
  sessionId: string;
  overallScore: number;
  itemResults: ItemEvaluation[];
  completedAt: string;
}

class TrainingWebSocketService {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;
  private isManualDisconnect = false;
  private currentTrackId: string | null = null;
  private currentSessionId: string | null = null;

  // Event handlers
  private onSessionStartedHandler: ((data: { sessionId: string; trackName: string; itemCount: number }) => void) | null = null;
  private onItemDeliveredHandler: ((item: TrainingItem) => void) | null = null;
  private onItemEvaluatedHandler: ((evaluation: ItemEvaluation) => void) | null = null;
  private onAllItemsDeliveredHandler: (() => void) | null = null;
  private onSessionCompletedHandler: ((result: SessionResult) => void) | null = null;
  private onErrorHandler: ((error: string) => void) | null = null;
  private onConnectionChangeHandler: ((connected: boolean) => void) | null = null;

  connect(trackId: string, sessionId?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isManualDisconnect = false;
      this.currentTrackId = trackId;

      const wsUrl = config.server.trainingUrl;
      console.log('🎓 Training WebSocket Service - Connection Details:');
      console.log('   Track ID:', trackId);
      console.log('   Session ID:', sessionId || 'Will be generated by server');
      console.log('   Timestamp:', new Date().toISOString());

      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = async () => {
        console.log('✅ Training WebSocket connected successfully');
        this.reconnectAttempts = 0;
        this.onConnectionChangeHandler?.(true);

        // Send authentication and start session
        try {
          const auth = getAuth();
          const token = await auth.currentUser?.getIdToken();

          if (!token) {
            throw new Error('No authentication token available');
          }

          const startMessage = {
            type: 'start_session',
            data: {
              trackId,
              token,
              sessionId, // Pass sessionId from URL to backend
            },
          };

          this.socket?.send(JSON.stringify(startMessage));
          resolve();
        } catch (error) {
          console.error('❌ Error sending start message:', error);
          reject(error);
        }
      };

      this.socket.onmessage = (event) => {
        try {
          const message: TrainingMessage = JSON.parse(event.data);

          switch (message.type) {
            case 'session_started':
              this.currentSessionId = message.data?.sessionId;
              this.onSessionStartedHandler?.(message.data);
              break;

            case 'item_delivered':
              this.onItemDeliveredHandler?.(message.data);
              break;

            case 'item_evaluated':
              this.onItemEvaluatedHandler?.(message.data);
              break;

            case 'session_completed':
              this.onSessionCompletedHandler?.(message.data);
              break;

            case 'all_items_delivered':
              // All items have been delivered and evaluated - ready to finish
              console.log('✅ All training items completed');
              this.onAllItemsDeliveredHandler?.();
              break;

            case 'error':
              console.error('❌ Training error:', message.data);
              this.onErrorHandler?.(message.data?.message || 'Unknown error');
              break;

            default:
              console.warn('⚠️ Unknown message type:', message.type);
          }
        } catch (error) {
          console.error('❌ Error parsing message:', error);
        }
      };

      this.socket.onerror = (error) => {
        console.error('❌ Training WebSocket error:', error);
        this.onConnectionChangeHandler?.(false);
        reject(error);
      };

      this.socket.onclose = () => {
        console.log('🔌 Training WebSocket closed');
        this.onConnectionChangeHandler?.(false);

        if (!this.isManualDisconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`🔄 Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts}...`);
          setTimeout(() => {
            if (this.currentTrackId) {
              this.connect(this.currentTrackId).catch((error) => {
                console.log(`❌ Reconnect attempt ${this.reconnectAttempts} failed:`, error);
              });
            }
          }, this.reconnectDelay * this.reconnectAttempts);
        } else if (!this.isManualDisconnect) {
          console.log('❌ Max reconnection attempts reached. Connection failed.');
        }
      };

      // Timeout fallback
      setTimeout(() => {
        if (this.socket?.readyState !== WebSocket.OPEN) {
          reject(new Error('Connection timeout'));
        }
      }, 10000);
    });
  }

  disconnect(): void {
    console.log('🔌 Disconnecting training WebSocket');
    this.isManualDisconnect = true;
    this.currentTrackId = null;
    this.currentSessionId = null;

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.onConnectionChangeHandler?.(false);
  }

  submitResponse(trackItemId: string, userResponse: string, responseTime: number): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('❌ Cannot submit response: WebSocket not connected');
      return;
    }

    const message = {
      type: 'submit_response',
      data: {
        trackItemId,
        userResponse,
        responseTime,
      },
    };
    this.socket.send(JSON.stringify(message));
  }

  sendMessage(message: { type: string; data: any }): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('❌ Cannot send message: WebSocket not connected');
      return;
    }

    this.socket.send(JSON.stringify(message));
  }

  endSession(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.error('❌ Cannot end session: WebSocket not connected');
      return;
    }

    const message = {
      type: 'end_session',
      data: {},
    };

    this.socket.send(JSON.stringify(message));
  }

  // Event handler setters
  onSessionStarted(handler: (data: { sessionId: string; trackName: string; itemCount: number }) => void): void {
    this.onSessionStartedHandler = handler;
  }

  onItemDelivered(handler: (item: TrainingItem) => void): void {
    this.onItemDeliveredHandler = handler;
  }

  onItemEvaluated(handler: (evaluation: ItemEvaluation) => void): void {
    this.onItemEvaluatedHandler = handler;
  }

  onAllItemsDelivered(handler: () => void): void {
    this.onAllItemsDeliveredHandler = handler;
  }

  onSessionCompleted(handler: (result: SessionResult) => void): void {
    this.onSessionCompletedHandler = handler;
  }

  onError(handler: (error: string) => void): void {
    this.onErrorHandler = handler;
  }

  onConnectionChange(handler: (connected: boolean) => void): void {
    this.onConnectionChangeHandler = handler;
  }

  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}

export const trainingWebSocketService = new TrainingWebSocketService();
